






























































































import { Component, Prop, Emit, Watch, Ref, Mixins } from 'vue-property-decorator';
import get from 'lodash/get';
import SInputBase from '@/components/UI/Form/SInputBase.vue';
import SAutocompleteInfo from './SAutocompleteInfo.vue';
import uniqBy from 'lodash/uniqBy';
import SFormElement from '../SFormElement.vue';

@Component({
  components: {
    SAutocompleteInfo,
    SInputBase,
  },
})
export default class SAutocomplete<ValueType, Option> extends Mixins(SFormElement) {
  @Prop({ required: true }) public value!: ValueType;
  @Prop({ type: Array, required: true }) private source!: Option[];
  @Prop({ type: [ String, Function ], default: () => (option: Option) => option }) private valueBy!: string | ((option: Option) => ValueType);
  @Prop({ type: [ String, Function ], default: () => (option: Option) => String(option) }) private filterBy!: string | ((option: Option) => string);
  @Prop([ String, Function ]) private displayOptionsBy?: string | ((option: Option) => string);
  @Prop([ String, Function ]) private displayInputBy?: string | ((option: Option) => string);
  @Prop() private keepAliveItem?: Option;
  @Prop(Boolean) private showAllOnOpen!: boolean;

  @Prop({ default: 3, type: Number }) private minChars!: number;

  @Prop(Function) private filter?: (source: Option[], term: string) => Option[];
  @Prop(Boolean) private mustBeSelected?: boolean;
  @Prop(Boolean) private selectWithTab?: boolean;
  @Prop(Boolean) private disabled?: boolean;

  @Prop({ type: Boolean, default: true }) private showFeedback?: boolean;

  private keepAliveItemInternal?: Option = this.keepAliveItem;

  protected get objectValue(): Option | undefined {
    return this.selectedOption;
  }

  // Open/closed handling

  private open = false;

  private toggleOpen(open: boolean): void {
    if (this.disabled) return;
    if (this.open === open) return;

    this.open = open;

    if (this.open) this.focusSelectedValue();
  }

  // Focused/unfocused handling

  private focused = false;

  @Watch('focused')
  private handleFocus(): void {
    this.toggleOpen(this.focused);

    if (!this.focused && this.mustBeSelected) {
      this.setInputValueFromValue();
    }
  }

  // Source handling

  private get sourceWithKeepAliveItem(): Option[] {
    if (!this.keepAliveItemInternal) return this.source;

    return uniqBy([
      this.keepAliveItemInternal,
      ...this.source,
    ], option => this.optionValue(option));
  }

  public setKeepAliveItem(value: Option) {
    this.keepAliveItemInternal = value;
  }

  private get filteredSource(): Option[] {
    if (this.lessThanMinCharsEntered) return [];
    if ( // if you click the autocomplete and have one option selected you should see all options.
      this.showAllOnOpen &&
      this.selectedOption &&
      this.inputDisplayValue(this.selectedOption) == this.inputValue
    ) {
      return this.sourceWithKeepAliveItem;
    }

    if (this.filter) {
      return this.filter(this.sourceWithKeepAliveItem, this.inputValue);
    } else {
      return this.sourceWithKeepAliveItem
        .filter(el => this.optionFilterValue(el).toLowerCase().includes(this.inputValue.toLowerCase()));
    }
  }

  // Options handling

  private get selectedOption(): Option | undefined {
    return this.sourceWithKeepAliveItem.find(o => this.isOptionSelected(o));
  }

  // TODO: find a better way to keep track of which option is selected.
  // When MustBeSelected is false, and user inputs something which is the same as an option value, suddenly it looks like that option is selected...
  private isOptionSelected(option: Option): boolean {
    return this.optionValue(option) === this.value;
  }

  private optionValue(option: Option): ValueType {
    if (typeof this.valueBy === 'string') {
      return get(option, this.valueBy);
    } else {
      return this.valueBy(option);
    }
  }

  private optionFilterValue(option: Option): string {
    if (typeof this.filterBy === 'string') {
      return get(option, this.filterBy);
    } else {
      return this.filterBy(option);
    }
  }

  private optionDisplayValue(option: Option): string {
    if (this.displayOptionsBy) {
      if (typeof this.displayOptionsBy === 'string') {
        return get(option, this.displayOptionsBy);
      } else {
        return this.displayOptionsBy(option);
      }
    } else {
      return this.optionFilterValue(option);
    }
  }

  private inputDisplayValue(option: Option): string {
    if (this.displayInputBy) {
      if (typeof this.displayInputBy === 'string') {
        return get(option, this.displayInputBy);
      } else {
        return this.displayInputBy(option);
      }
    } else {
      return this.optionDisplayValue(option);
    }
  }

  @Emit('input')
  public selectOption(option: Option): ValueType {
    // Close in next tick, because change on inputValue would open the options again otherwise.
    this.$nextTick().then(() => this.toggleOpen(false));

    this.$emit('select', option);
    return this.optionValue(option);
  }

  // SInput internals

  @Ref() private readonly inputRef!: SInputBase;

  private inputValue = '';

  @Watch('value', { immediate: true })
  private setInputValueFromValue(): void {
    if (!this.mustBeSelected) {
      if (this.selectedOption) {
        this.inputValue = this.inputDisplayValue(this.selectedOption);
      } else {
        this.inputValue = this.value ? String(this.value) : '';
      }
    } else {
      this.inputValue = this.selectedOption ? this.inputDisplayValue(this.selectedOption) : '';
    }
  }

  @Watch('inputValue')
  private handleInputChange(): void {
    if (this.focused) this.toggleOpen(true);
    if (!this.mustBeSelected) {
      const innerSelectedOption = this.sourceWithKeepAliveItem
        .find(o => this.optionDisplayValue(o) === this.inputValue);

      if (innerSelectedOption) {
        this.$emit('input', this.optionValue(innerSelectedOption));
      } else {
        this.$emit('input', this.inputValue);
      }
    }
    if (this.inputValue === '') this.$emit('empty');
  }

  private get listeners() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { input, select, ...listeners } = this.$listeners;
    return listeners;
  }

  public focus(): void {
    this.inputRef.focus();
  }

  @Watch('open')
  @Watch('inputValue')
  private emitAsyncSearch(): void {
    if (this.open) this.$emit('asyncSearch', this.inputValue);
  }

  private get lessThanMinCharsEntered(): boolean {
    return this.inputValue.length < this.minChars;
  }

  // Focus

  private focusedIndex = 0;

  private focusSelectedValue(): void {
    this.focusedIndex = this.selectedOption ? this.filteredSource.indexOf(this.selectedOption) : 0;
  }

  private changeFocus(step: number): void {
    this.focusedIndex += step;

    if (this.focusedIndex >= this.filteredSource.length) this.focusedIndex = 0;
    if (this.focusedIndex < 0) this.focusedIndex = this.filteredSource.length - 1;
  }

  private selectFocusedOption(): void {
    const focusedOption = this.filteredSource[this.focusedIndex];
    if (focusedOption) this.selectOption(focusedOption);
  }

  // Styles

  @Prop({ type: Boolean, default: true }) private fullSize!: boolean;
  @Prop(Boolean) private inline?: boolean;
  @Prop({ type: String, default: 'search' }) private icon!: string;
  @Prop({ type: Boolean, default: false }) private hideIconWhenUsed?: boolean;
  @Prop({ type: Number }) private maxHeight?: number;
  @Prop({ type: Number, default: 200 }) private maxWidth!: number;

  private optionsHolderStyles = {
    left: '0',
    top: '0',
    minWidth: '0',
    maxWidth: '200px',
    maxHeight: 'auto',
  }

  // Polling is added to fix the position of autocomplete dropdown,
  // in case the autocomplete input moves (example: scroll, popup changed position).
  private positionPoll: NodeJS.Timeout | null = null;

  @Watch('open')
  private positionPolling(): void {
    if (this.open) {
      this.updatePosition();
      this.positionPoll = setInterval(this.updatePosition, 25);
    } else if (this.positionPoll) {
      // when autocomplete is present in DOM but menu is not open
      // we need to clear the polling
      clearInterval(this.positionPoll);
      this.positionPoll = null;
    }
  }

  public destroyed() {
    // when autocomplete is destroyed interval clearing in positionPolling will not happen
    // example of this would be ChangeUnit component
    if (this.positionPoll) clearInterval(this.positionPoll);
  }

  private updatePosition(): void {
    const inputRect = (this.inputRef.inputHolderRef.getBoundingClientRect() as DOMRect);
    this.optionsHolderStyles.top = `${inputRect.y + inputRect.height + (window.pageYOffset || document.documentElement.scrollTop)}px`;
    this.optionsHolderStyles.left = `${inputRect.x + (window.pageXOffset || document.documentElement.scrollLeft)}px`;
    this.optionsHolderStyles.minWidth = `${inputRect.width}px`;
    this.optionsHolderStyles.maxWidth = `${Math.max(inputRect.width, this.maxWidth)}px`;
    const maxHeight = this.maxHeight ? this.maxHeight : window.innerHeight - (inputRect.top + inputRect.height + 24);
    this.optionsHolderStyles.maxHeight = `${Math.max(maxHeight, 200)}px`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onScroll(event: any): void {
    if (event.target) {
      const { clientHeight, scrollHeight, scrollTop } = event.target;
      if (scrollTop + clientHeight * 2 >= scrollHeight) {
        this.$emit('scroll-near-end', this.inputValue);
      }
    }
  }

  private get displayedIcon(): string {
    if (this.hideIconWhenUsed && this.inputValue.length > 0) return '';

    return this.icon || '';
  }

  @Ref() private readonly autocompleteListRef!: HTMLElement;

  @Watch('open')
  private scrollOptionIntoView() {
    this.$nextTick().then(() => {
      if (!this.open || !this.selectedOption) return;
      if (!this.autocompleteListRef) return;
      const selectedOption = this.autocompleteListRef.getElementsByTagName('li')[this.focusedIndex];
      this.autocompleteListRef.scrollIntoView({ block: 'nearest' });
      selectedOption.scrollIntoView({ block: 'nearest' });
    });
  }
}
